// Tronco: resistência isométrica dos flexores, extensores e da musculatura lateral.
//
// A leitura é por RAZÃO entre os testes, não pelo tempo absoluto. É isso que separa uma
// fraqueza real de um atleta que simplesmente tem pouca resistência geral: o que importa
// é o equilíbrio entre os grupos musculares que estabilizam a coluna.
//
// Referência: McGill, Childs & Liebenson (1999) — Endurance times for low back
// stabilization exercises. Arch Phys Med Rehabil, 80(8), 941-944.

const { toNumber, round } = require('./primitives');

// Sorensen (extensores) é o denominador: os outros testes são lidos em relação a ele.
const FLEXION_EXTENSION_MAX = 1.0;   // trunk flexor endurance test / Sorensen
const LATERAL_EXTENSION_MAX = 0.75;  // prancha lateral / Sorensen
const LATERAL_BALANCE_MAX_PCT = 5;   // diferença aceitável entre lateral D e E

function trunkExtensorEndurance(info) {
    return toNumber(info.extensor_endurance_test_lumbar);
}

// Flexores no protocolo de McGill é o trunk flexor endurance test (sentado a 60°), não a
// prancha frontal — a prancha continua sendo medida, mas fora das razões.
function trunkFlexorEndurance(info) {
    return toNumber(info.trunk_flexor_endurance);
}

// Medido e exibido, sem entrar em nenhuma razão.
function trunkFrontalPlank(info) {
    return toNumber(info.prancha_frontal);
}

function trunkLateralEndurance(info, side) {
    return toNumber(info[`prancha_lateral_${side}`]);
}

function ratio(value, reference) {
    if (value === null || reference === null || reference === 0) return null;
    return round(value / reference, 2);
}

// Razão flexão/extensão acima de 1.0: flexores desproporcionalmente resistentes em
// relação aos extensores, padrão associado a dor lombar.
function trunkFlexionExtensionRatio(info) {
    return ratio(trunkFlexorEndurance(info), trunkExtensorEndurance(info));
}

function trunkLateralExtensionRatio(info, side) {
    return ratio(trunkLateralEndurance(info, side), trunkExtensorEndurance(info));
}

// Desequilíbrio entre os lados, em % do maior — o próprio atleta é a referência.
function trunkLateralBalance(info) {
    const right = trunkLateralEndurance(info, 'right');
    const left = trunkLateralEndurance(info, 'left');
    if (right === null || left === null) return null;

    const highest = Math.max(right, left);
    if (highest === 0) return null;

    return round((Math.abs(right - left) / highest) * 100);
}

// Cada razão vira {value, status, label}. `status` é 'ok' | 'risk' | null (não calculável)
// para a tela escolher a cor sem repetir a regra.
function evaluate(value, isOk, okLabel, riskLabel) {
    if (value === null) return { value: null, status: null, label: 'Não calculado' };
    return isOk(value)
        ? { value, status: 'ok', label: okLabel }
        : { value, status: 'risk', label: riskLabel };
}

function trunkScores(info) {
    const flexionExtension = trunkFlexionExtensionRatio(info);
    const lateralRight = trunkLateralExtensionRatio(info, 'right');
    const lateralLeft = trunkLateralExtensionRatio(info, 'left');
    const balance = trunkLateralBalance(info);

    return {
        extensorEndurance: trunkExtensorEndurance(info),
        flexorEndurance: trunkFlexorEndurance(info),
        frontalPlank: trunkFrontalPlank(info),
        lateralRightEndurance: trunkLateralEndurance(info, 'right'),
        lateralLeftEndurance: trunkLateralEndurance(info, 'left'),

        flexionExtension: evaluate(
            flexionExtension,
            (value) => value < FLEXION_EXTENSION_MAX,
            `Adequado — abaixo de ${FLEXION_EXTENSION_MAX.toFixed(2)}`,
            `Risco — flexores desproporcionais aos extensores (limite ${FLEXION_EXTENSION_MAX.toFixed(2)})`
        ),
        lateralRight: evaluate(
            lateralRight,
            (value) => value < LATERAL_EXTENSION_MAX,
            `Adequado — abaixo de ${LATERAL_EXTENSION_MAX.toFixed(2)}`,
            `Risco — lateral direita desproporcional aos extensores (limite ${LATERAL_EXTENSION_MAX.toFixed(2)})`
        ),
        lateralLeft: evaluate(
            lateralLeft,
            (value) => value < LATERAL_EXTENSION_MAX,
            `Adequado — abaixo de ${LATERAL_EXTENSION_MAX.toFixed(2)}`,
            `Risco — lateral esquerda desproporcional aos extensores (limite ${LATERAL_EXTENSION_MAX.toFixed(2)})`
        ),
        lateralBalance: evaluate(
            balance,
            (value) => value <= LATERAL_BALANCE_MAX_PCT,
            `Adequado — diferença até ${LATERAL_BALANCE_MAX_PCT}%`,
            `Risco — desequilíbrio entre os lados acima de ${LATERAL_BALANCE_MAX_PCT}%`
        )
    };
}

// Só as razões fora da faixa, para o relatório e a conclusão citarem o que importa.
function trunkFindings(info) {
    const scores = trunkScores(info);

    return ['flexionExtension', 'lateralRight', 'lateralLeft', 'lateralBalance']
        .filter((key) => scores[key].status === 'risk')
        .map((key) => scores[key].label);
}

module.exports = {
    FLEXION_EXTENSION_MAX,
    LATERAL_EXTENSION_MAX,
    LATERAL_BALANCE_MAX_PCT,
    trunkExtensorEndurance,
    trunkFlexorEndurance,
    trunkFrontalPlank,
    trunkLateralEndurance,
    trunkFlexionExtensionRatio,
    trunkLateralExtensionRatio,
    trunkLateralBalance,
    trunkScores,
    trunkFindings
};
